/**
 * Challenge tasks/habits tab — professional cards with progress rings,
 * frequency badges, details. Creator can add/delete habits.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  ListChecks, Target, Clock4, Hourglass, Trash2, Plus, Camera,
} from 'lucide-react'

import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'
import { useDeleteChallengeHabit } from '@turnifly/shared/hooks'
import { useChallengeStore } from '@turnifly/shared/stores'
import { useHomeStore } from '@turnifly/shared/stores'
import type {
  ChallengeHabit,
  ChallengeHabitProgress,
} from '@turnifly/shared/types'

// ── Props ────────────────────────────────────────────────────

interface ChallengeTasksTabProps {
  challengeId: string
  habits: ChallengeHabit[]
  userProgress: ChallengeHabitProgress[]
  isMember: boolean
  isCreator: boolean
}

// ── Ring constants ───────────────────────────────────────────

const RS = 36
const RSW = 3.5
const RR = (RS - RSW) / 2
const RC = 2 * Math.PI * RR

function rateColor(rate: number): string {
  if (rate >= 80) return '#22C55E'
  if (rate >= 60) return '#EAB308'
  if (rate >= 40) return '#F97316'
  return '#EF4444'
}

// ── Habit task card ──────────────────────────────────────────

function HabitTaskCard({ habit, progress, isMember, isCreator, index, onDelete }: {
  habit: ChallengeHabit
  progress: ChallengeHabitProgress | undefined
  isMember: boolean
  isCreator: boolean
  index: number
  onDelete: (id: string) => void
}): React.ReactElement {
  const { t } = useTranslation()

  const rate = progress?.completion_rate ?? 0
  const dashOffset = RC - (rate / 100) * RC
  const color = rateColor(rate)
  const freqKey = `challenges.profile.frequency.${habit.frequency}`
  const freqLabel = t(freqKey, { defaultValue: habit.frequency })

  const hasTimeStart = !!habit.time_start
  const hasDuration = !!habit.time_duration && habit.time_duration > 0
  const hasTarget = habit.value_required && !!habit.value_target
  const hasProof = habit.proof_types && habit.proof_types.length > 0

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: index * 0.04 }}
      className="flex items-start gap-3 rounded-2xl bg-white/[0.04] p-3.5"
    >
      {/* Emoji icon */}
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/[0.08] bg-white/[0.04] text-xl">
        {habit.icon}
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold text-white">
          {habit.name}
        </p>

        {/* Badges row */}
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <span className="rounded-full bg-white/[0.06] px-2 py-0.5 text-[10px] font-medium text-[var(--color-text-hint)]">
            {freqLabel}
          </span>
          {hasTarget && (
            <span className="flex items-center gap-0.5 rounded-full bg-[var(--color-success)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--color-success)]">
              <Target size={9} />
              {habit.value_target} {habit.value_unit ?? ''}
            </span>
          )}
          {hasTimeStart && (
            <span className="flex items-center gap-0.5 rounded-full bg-[var(--color-accent)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--color-accent)]">
              <Clock4 size={9} />
              {habit.time_start}
            </span>
          )}
          {hasDuration && (
            <span className="flex items-center gap-0.5 rounded-full bg-[var(--color-warning)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--color-warning)]">
              <Hourglass size={9} />
              {habit.time_duration} min
            </span>
          )}
          {hasProof && (
            <span className="flex items-center gap-0.5 rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--color-primary)]">
              <Camera size={9} />
            </span>
          )}
        </div>

        {/* Progress bar (members only) */}
        {isMember && progress && (
          <div className="mt-2.5 flex items-center gap-2">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/[0.04]">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${rate}%` }}
                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 + index * 0.04 }}
                className="h-full rounded-full"
                style={{ background: `linear-gradient(90deg, ${color}, ${color}90)` }}
              />
            </div>
            <span className="text-[10px] font-medium tabular-nums text-[var(--color-text-hint)]">
              {progress.completed_count}/{progress.total_applicable}
            </span>
          </div>
        )}
      </div>

      {/* Right: ring (member) or delete (creator) */}
      {isMember && progress ? (
        <div className="relative shrink-0">
          <svg width={RS} height={RS} className="-rotate-90">
            <circle
              cx={RS / 2} cy={RS / 2} r={RR}
              fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={RSW}
            />
            <circle
              cx={RS / 2} cy={RS / 2} r={RR}
              fill="none" stroke={color} strokeWidth={RSW} strokeLinecap="round"
              strokeDasharray={RC} strokeDashoffset={dashOffset}
              style={{ filter: `drop-shadow(0 0 3px ${color}30)` }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[10px] font-bold tabular-nums text-white">
              {Math.round(rate)}
            </span>
          </div>
        </div>
      ) : isCreator ? (
        <button
          type="button"
          onClick={() => { onDelete(habit.id); hapticImpact('light') }}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[var(--color-danger)]/60 transition-colors hover:bg-[var(--color-danger)]/10 hover:text-[var(--color-danger)]"
        >
          <Trash2 size={14} />
        </button>
      ) : null}
    </motion.div>
  )
}

// ── Main component ───────────────────────────────────────────

export function ChallengeTasksTab({
  challengeId,
  habits,
  userProgress,
  isMember,
  isCreator,
}: ChallengeTasksTabProps): React.ReactElement {
  const { t } = useTranslation()
  const deleteMutation = useDeleteChallengeHabit(challengeId)

  const progressMap = useMemo(
    () => new Map(userProgress.map((p) => [p.habit_id, p])),
    [userProgress],
  )

  const handleDelete = (habitId: string): void => {
    deleteMutation.mutate(habitId)
  }

  const handleAddHabit = (): void => {
    useChallengeStore.getState().setAddHabitChallengeId(challengeId)
    useHomeStore.getState().setFabOpen(true)
    hapticImpact('light')
  }

  if (habits.length === 0) {
    return (
      <div className="px-4 py-16 text-center">
        <ListChecks
          size={36}
          className="mx-auto mb-3 text-[var(--color-text-hint)]/20"
        />
        <p className="text-[13px] text-[var(--color-text-hint)]">
          {t('challenges.profile.noTasks')}
        </p>
        {isCreator && (
          <button
            type="button"
            className="mx-auto mt-4 flex items-center gap-2 rounded-xl bg-[var(--color-primary)] px-5 py-2.5 text-[13px] font-semibold text-white shadow-[0_0_16px_rgba(0,97,255,0.25)] transition-transform active:scale-[0.96]"
            onClick={handleAddHabit}
          >
            <Plus size={16} />
            {t('challenges.profile.addTask')}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-2 px-4 py-3">
      {habits.map((habit, i) => (
        <HabitTaskCard
          key={habit.id}
          habit={habit}
          progress={progressMap.get(habit.id)}
          isMember={isMember}
          isCreator={isCreator}
          index={i}
          onDelete={handleDelete}
        />
      ))}

      {/* Add habit button (creator only) */}
      {isCreator && (
        <motion.button
          type="button"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: habits.length * 0.04 + 0.2 }}
          className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-white/[0.08] py-3.5 text-[13px] font-medium text-[var(--color-primary)] transition-colors hover:border-[var(--color-primary)]/30 hover:bg-[var(--color-primary)]/5"
          onClick={handleAddHabit}
        >
          <Plus size={16} />
          {t('challenges.profile.addTask')}
        </motion.button>
      )}
    </div>
  )
}
